import { memo, useMemo } from 'react';
import { businessAreaLabel } from './executive';
import type { CompanyNewsAnalysis } from './types';

interface MarketAnalyticsProps {
  data: CompanyNewsAnalysis;
  daysBack: number;
}

interface SentimentBar {
  label: string;
  count: number;
  color: string;
  bg: string;
}

function CoverageSentiment({ articles }: { articles: CompanyNewsAnalysis['articles'] }) {
  const bars = useMemo<SentimentBar[]>(() => {
    const counts = { positive: 0, cautious: 0, negative: 0, neutral: 0 };
    for (const a of articles) {
      const s = (a.sentiment ?? '').toLowerCase();
      if (['bullish', 'positive'].includes(s)) counts.positive++;
      else if (['cautious', 'mixed'].includes(s)) counts.cautious++;
      else if (['bearish', 'negative'].includes(s)) counts.negative++;
      else counts.neutral++;
    }
    const total = articles.length || 1;
    return [
      { label: 'Positive', count: counts.positive, color: 'bg-emerald-500', bg: 'bg-emerald-50' },
      { label: 'Cautious', count: counts.cautious, color: 'bg-amber-400', bg: 'bg-amber-50' },
      { label: 'Negative', count: counts.negative, color: 'bg-red-500', bg: 'bg-red-50' },
      { label: 'Neutral', count: counts.neutral, color: 'bg-neutral-300', bg: 'bg-neutral-100' },
    ]
      .filter((b) => b.count > 0)
      .map((b) => ({ ...b, pct: Math.round((b.count / total) * 100) }));
  }, [articles]);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-neutral-500">
        Coverage Sentiment
      </p>
      <div className="space-y-3">
        {(bars as (SentimentBar & { pct: number })[]).map((bar) => (
          <div key={bar.label}>
            <div className="mb-1 flex items-center justify-between text-[11px]">
              <span className="text-neutral-600">{bar.label}</span>
              <span className="font-semibold tabular-nums text-neutral-800">{bar.pct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className={`h-full rounded-full transition-all ${bar.color}`}
                style={{ width: `${bar.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BusinessThemes({ insights }: { insights: CompanyNewsAnalysis['key_insights'] }) {
  const themes = useMemo(() => {
    const areas = insights
      .filter((i) => !i.contrarian)
      .slice(0, 8)
      .map((i) => businessAreaLabel(`${i.insight_label} ${i.implication ?? ''}`));
    return [...new Set(areas)].slice(0, 5);
  }, [insights]);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-neutral-500">
        Key Business Themes
      </p>
      {themes.length === 0 ? (
        <p className="text-xs text-neutral-400">No themes detected.</p>
      ) : (
        <ul className="space-y-3">
          {themes.map((theme, i) => (
            <li key={i} className="flex items-center gap-3">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[10px] font-bold text-primary-700">
                {i + 1}
              </span>
              <span className="text-sm text-neutral-700">{theme}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CoverageSummary({
  data,
  daysBack,
}: {
  data: CompanyNewsAnalysis;
  daysBack: number;
}) {
  const highSignal = data.articles.filter((a) =>
    ['high', 'strong'].includes((a.signal_strength ?? '').toLowerCase())
  ).length;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-neutral-500">
        Coverage Summary
      </p>
      <div className="space-y-4">
        <div>
          <p className="text-3xl font-bold tracking-tight text-neutral-900">
            {data.articles.length}
          </p>
          <p className="mt-0.5 text-xs text-neutral-500">articles in last {daysBack} days</p>
        </div>
        {highSignal > 0 && (
          <div>
            <p className="text-3xl font-bold tracking-tight text-primary-700">{highSignal}</p>
            <p className="mt-0.5 text-xs text-neutral-500">high-impact stories</p>
          </div>
        )}
        {data.risks.length > 0 && (
          <div>
            <p className="text-3xl font-bold tracking-tight text-red-600">{data.risks.length}</p>
            <p className="mt-0.5 text-xs text-neutral-500">risk factors identified</p>
          </div>
        )}
      </div>
    </div>
  );
}

function MarketAnalyticsBase({ data, daysBack }: MarketAnalyticsProps) {
  if (data.articles.length === 0 && data.key_insights.length === 0) return null;

  return (
    <section>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-neutral-900">Market & Coverage Analysis</h2>
        <p className="mt-0.5 text-xs text-neutral-500">
          Intelligence summary from {data.articles.length} articles · last {daysBack} days
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <CoverageSentiment articles={data.articles} />
        <BusinessThemes insights={data.key_insights} />
        <CoverageSummary data={data} daysBack={daysBack} />
      </div>
    </section>
  );
}

export const MarketAnalytics = memo(MarketAnalyticsBase);
